import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { formatCurrency } from "@plasma/utils/money-formatters";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { modifiersCreateGroup, modifiersGetModifierGroup } from "~/api-refs";
import { ApplyNewModifierForm } from "~/components/menus/modifier-modal/apply-new-modifier-form";
import { ModifierForm } from "~/components/menus/modifier-modal/modifier-form";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { parseEnumValue } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";
import type { ModifierFormSchema } from "~/lib/validators/modifiers";
import { isModifierType } from "~/lib/validators/modifiers";

// V4.1: Route-based duplicate modifier modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/modifiers/$modifierGroupId/duplicate"
)({
  component: DuplicateModifierModal,
});

function DuplicateModifierModal() {
  const { menuId, modifierGroupId } = Route.useParams();
  const navigate = useNavigate();
  const [modifierFormData, setModifierFormData] =
    useState<ModifierFormSchema | null>(null);

  // Fetch the modifier group to duplicate
  const { data: sourceModifierGroup, isPending } = useQuery(
    convexQuery(modifiersGetModifierGroup, {
      modifierGroupId: toConvexTableId("modifierGroups", modifierGroupId),
    })
  );

  const createModifierGroupMutation = useMutation({
    mutationFn: (data: {
      data: {
        name: string;
        minimum: number;
        maximum: number;
        modifiers: {
          name: string;
          additionalCost: number;
          isOutOfStock: boolean;
          canBeMultiplied: boolean;
          sortOrder: number;
        }[];
        subModifierGroups: {
          name: string;
          maximum: number;
          minimum: number;
          sortOrder: number;
          modifiers: {
            name: string;
            additionalCost: number;
            isOutOfStock: boolean;
            canBeMultiplied: boolean;
          }[];
        }[];
      };
      applyModifierGroupTo: {
        categories: Id<"categories">[];
        items: Id<"items">[];
      };
    }) => {
      return convexClient.mutation(modifiersCreateGroup, data);
    },
    onSuccess: (result) => {
      if (result?.id) {
        ProductEvents.modifierGroupDuplicated(modifierGroupId, result.id);
      }
    },
  });

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/modifiers/${modifierGroupId}` });
  };

  const handleFinished = () => {
    handleClose();
  };

  const handleApplySubmit = async (applyTo: {
    categories: string[];
    items: string[];
  }) => {
    if (createModifierGroupMutation.isPending || !modifierFormData) {
      return;
    }
    try {
      const modifiers = modifierFormData.modifiersList
        .filter((modifier) => modifier.modifierType === "modifier")
        .map((modifier) => ({
          name: modifier.name,
          additionalCost: Number(modifier.data.additionalCost),
          isOutOfStock: modifier.data.isOutOfStock,
          canBeMultiplied: modifier.data.canBeMultiplied,
          sortOrder: modifier.sortOrder,
        }));

      const subModifierGroups = modifierFormData.modifiersList
        .filter((modifier) => modifier.modifierType === "sub_modifier")
        .map((submodifier) => ({
          name: submodifier.name,
          maximum: submodifier.data.maximum,
          minimum: submodifier.data.minimum,
          sortOrder: submodifier.sortOrder,
          modifiers: submodifier.data.modifiers.map((modifier) => ({
            name: modifier.name,
            additionalCost: Number(modifier.additionalCost),
            isOutOfStock: modifier.isOutOfStock,
            canBeMultiplied: modifier.canBeMultiplied,
          })),
        }));

      await createModifierGroupMutation.mutateAsync({
        data: {
          name: modifierFormData.name,
          minimum: modifierFormData.minimum,
          maximum: modifierFormData.maximum,
          modifiers,
          subModifierGroups,
        },
        applyModifierGroupTo: {
          categories: applyTo.categories.map((c) =>
            toConvexTableId("categories", c)
          ),
          items: applyTo.items.map((i) => toConvexTableId("items", i)),
        },
      });
      handleFinished();
    } catch (error) {
      logger.exception("Failed to create modifier group", error);
    }
  };

  if (isPending || !sourceModifierGroup) {
    return (
      <ModalLoadingDialog
        message="Loading modifier group..."
        onOpenChange={handleClose}
      />
    );
  }

  // Transform source modifier group to form schema for duplicate
  const sourceFormData: ModifierFormSchema = {
    id: sourceModifierGroup._id,
    name: sourceModifierGroup.name,
    minimum: sourceModifierGroup.minimum,
    maximum: sourceModifierGroup.maximum,
    type: "modifier",
    sortOrder: 0,
    parentModifierGroupId: null,
    modifiersList: [
      ...sourceModifierGroup.modifiers.map((modifier) => ({
        itemId: String(modifier._id),
        name: modifier.name,
        accordionOpen: true,
        sortOrder: modifier.sortOrder,
        modifierType: "modifier" as const,
        data: {
          additionalCost: formatCurrency(modifier.additionalCost, {
            hideCurrencySign: true,
          }),
          canBeMultiplied: modifier.canBeMultiplied,
          isOutOfStock: modifier.isOutOfStock,
        },
      })),
      ...sourceModifierGroup.subModifierGroups.map((subModifier) => ({
        itemId: String(subModifier._id),
        modifierType: "sub_modifier" as const,
        name: subModifier.name,
        sortOrder: subModifier.sortOrder,
        accordionOpen: true,
        data: {
          minimum: subModifier.minimum,
          maximum: subModifier.maximum,
          type: parseEnumValue(
            subModifier.type,
            isModifierType,
            "sub_modifier"
          ),
          parentModifierGroupId: subModifier.parentModifierGroupId
            ? String(subModifier.parentModifierGroupId)
            : null,
          modifiers: subModifier.modifiers.map((modifier) => ({
            itemId: String(modifier._id),
            name: modifier.name,
            sortOrder: modifier.sortOrder,
            additionalCost: formatCurrency(modifier.additionalCost, {
              hideCurrencySign: true,
            }),
            isOutOfStock: modifier.isOutOfStock,
            canBeMultiplied: modifier.canBeMultiplied,
          })),
        },
      })),
    ],
    modifiers: [],
    subModifierGroups: [],
  };

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-176 overflow-x-hidden">
        {modifierFormData ? (
          <ApplyNewModifierForm
            isPending={createModifierGroupMutation.isPending}
            onSubmit={handleApplySubmit}
          />
        ) : (
          <>
            <DialogHeader className="items-center space-y-0">
              <DialogTitle>Duplicate Modifier Group</DialogTitle>
              <DialogDescription>
                Create a new modifier group based on "{sourceModifierGroup.name}
                "
              </DialogDescription>
            </DialogHeader>
            <ModifierForm
              editMode={false}
              onSubmit={setModifierFormData}
              values={sourceFormData}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
